import clsx from 'clsx'
import SidebarFloatingAd from 'components/ad/SidebarFloatingAd'
import NC from 'nc/NC'
import React from 'react'
import TopBanner from 'shared-components/ad/TopBanner'
import OfferButtonLink from 'shared-components/buttons/OfferButtonLink'
import { useAffilate } from 'shared-components/hooks/use-affilate'
import { useTaglessAd } from 'shared-components/hooks/use-tagless-ad'
import PostNavigation from 'shared-components/layout/PostNavigation'
import ScrollEventsListener from 'shared-components/service/ScrollEventsListener'
import styleUtils from 'shared-components/styles/util.module.css'
import AdaptiveThreeColumnLayout from 'shared-components/ui/AdaptiveThreeColumnLayout'
import BadgedShort from 'shared-components/ui/BadgedShort'
import BreadCrumbs from 'shared-components/ui/BreadCrumbs'
import ArticleConversation from 'shared-components/comments/conversation/ArticleConversation'
import ConversationRailCard from 'shared-components/comments/conversation/ConversationRailCard'
import Disclaimer from 'shared-components/ui/Disclaimer'
import GoogleButtons from 'shared-components/ui/GoogleButtons'
import IconedListItem from 'shared-components/ui/IconedListItem'
import PostShortLine from 'shared-components/ui/PostShortLine'
import PostTags from 'shared-components/ui/PostTags'
import PostTitle from 'shared-components/ui/PostTitle'
import ReviewHeroLow from 'shared-components/ui/ReviewHeroLow'
import ScoreLine from 'shared-components/ui/ScoreLine'
import SidedCategoryMenu from 'shared-components/ui/SidedCategoryMenu'
import SplitterLine from 'shared-components/ui/SplitterLine'
import Check from 'shared-svg/Check.svg'
import Close from 'shared-svg/CloseBold.svg'
import { DSReviewPageAA } from '../types.aa.ds'
import stylesCommon from './PostTemplate.ReviewTemplate.module.css'
import styles from './ReviewTemplate.module.css'
import { useArticleViewed } from 'shared-components/hooks/use-article-viewed'
import UnderTitleAds from 'components/ad/UnderTitleAds'

interface ReviewTemplateProps {
  page: DSReviewPageAA
}

const ReviewTemplate: React.VFC<ReviewTemplateProps> = ({ page }) => {
  useTaglessAd()
  useAffilate(page.productBars)

  return (
    <>
      <ScrollEventsListener />
      <Disclaimer />
      <div
        className={styleUtils.wrapper}
        ref={useArticleViewed({
          articleId: page.meta?.postId?.toString() ?? null,
          title: page.title,
          url: page.pagePath,
          categoryLevel: 1,
          categoryTitle: page.meta.categories[0]?.label ?? '',
          author: page.authors.authoredBy.map(author => author.name).join(', '),
          wordCount: parseInt(page.meta.dataLayer?.word_count?.toString() ?? '0', 10),
          date: page.meta.dataLayer?.original_pub_date?.toString() ?? '',
        })}
      >
        <BreadCrumbs list={page.breadcrumbs} className={stylesCommon.breadCrumbs} />
        <ReviewHeroLow post={page}>
          <PostTitle title={page.title} subtitle={page.subtitle} />
          <PostShortLine post={page} className={styles.shortLine} />
          <TopBanner />
          <UnderTitleAds ads={page.underTitleAds} />
          <SplitterLine className={clsx(styleUtils.mobileFull, styles.heroSplitter)} />
        </ReviewHeroLow>
        <div
          className={clsx(styles.infoGrid, {
            [styles.infoGridWithScores]: page.review.scores.length > 0,
          })}
        >
          <BadgedShort
            className={styles.badgedShort}
            image={page.badges.length ? page.badges[0] : null}
            score={page.review.device.score}
            bottomLine={page.review.bottomLine}
            title={page.review.device.name}
          />
          {page.offers ? (
            <div className={styles.retailOffers}>
              <h3 className={styles.h3Title}>{page.offers.title}</h3>
              {page.offers.items.map((item, index) => (
                <OfferButtonLink
                  data-sel="review-buy-link"
                  key={index}
                  {...item.pLink}
                  className={styles.offerLink}
                >
                  {item.label}
                </OfferButtonLink>
              ))}
            </div>
          ) : null}
          <div className={clsx(styles.features, styles.featuresPositive)}>
            <h3 className={styles.h3Title}>{page.table.positive.title}</h3>
            {page.table.positive.list.map((item, index) => (
              <IconedListItem icon={Check} key={index} iconClassName={styles.iconPositive}>
                {item}
              </IconedListItem>
            ))}
          </div>
          <div className={clsx(styles.features, styles.featuresNegative)}>
            <h3 className={styles.h3Title}>{page.table.negative.title}</h3>
            {page.table.negative.list.map((item, index) => (
              <IconedListItem icon={Close} key={index} iconClassName={styles.iconNegative}>
                {item}
              </IconedListItem>
            ))}
          </div>
          {page.review.scores.length ? (
            <div className={styles.scores}>
              <h3 className={styles.h3Title}>{'Our scores'}</h3>
              {page.review.scores.map((item, index) => (
                <ScoreLine key={index} score={item} />
              ))}
            </div>
          ) : null}
          <SplitterLine className={styles.splitter} />
        </div>

        <AdaptiveThreeColumnLayout
          left={
            page.sideMenu.length ? (
              <SidedCategoryMenu
                className={stylesCommon.sidebarPostNav}
                title="Go deeper"
                menu={page.sideMenu}
              />
            ) : null
          }
          content={
            <>
              <BadgedShort
                className={styles.badgedShortContent}
                image={page.badges.length ? page.badges[0] : null}
                score={page.review.device.score}
                bottomLine={page.review.bottomLine}
                title={page.review.device.name}
              />

              <GoogleButtons data={page.googleButtons} />

              {page.blocks.map((item, index) => (
                <NC key={index + 1} data={item} tpl="text" index={index} />
              ))}

              <PostTags meta={page.meta} className={stylesCommon.postTags} />

              {page.pagePath && page.meta.engagementContainerId ? (
                <ArticleConversation className={stylesCommon.comments} />
              ) : null}
            </>
          }
          sidebar={
            <PostNavigation>
              {[...page.sidebarContent, ...page.sidebarAds].map((item, index) => (
                <NC key={index} data={item} tpl="sidebar" index={index} />
              ))}
              {/* After every WordPress sidebar widget (the trust box arrives in sidebarAds as an
                  nc-multi), before the sticky Playwire ads: the widgets scroll away above the card and
                  never pass under the stuck strip; the ads still pin under it. */}
              <ConversationRailCard />
              <SidebarFloatingAd />
            </PostNavigation>
          }
        />
        {/* <RecommendAA
          containerClassName={stylesCommon.related}
          itemClassName={styleRelatedCard.relatedCard}
          title="More from Android Authority"
        /> */}
      </div>
    </>
  )
}

export default ReviewTemplate
